import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, ShoppingBag, X, User, Heart } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { Input } from "../components/ui/input";
import { useCart } from "../context/CartContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { LOGO_URL, suggest, fetchSiteContent } from "../lib/api";

const NAV = [
  { label: "Shop", to: "/shop" },
  { label: "Shirts", to: "/shop?category=shirts" },
  { label: "Short Kurtas", to: "/shop?category=kurtas" },
  { label: "Long Kurtas", to: "/shop?category=long-kurta" },
  { label: "Journal", to: "/blog" },
  { label: "About", to: "/blog" },
];

export default function Navbar() {
  const { count, setIsOpen } = useCart();
  const { user } = useCustomerAuth();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ products: [], posts: [] });
  const [announcement, setAnnouncement] = useState(
    "Complimentary shipping across India · Festive edit now live"
  );
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSiteContent()
      .then((c) => {
        if (!c) return;
        if (c.announcement_bar) setAnnouncement(c.announcement_bar);
        const now = new Date();
        const from = c.announcement_visible_from ? new Date(c.announcement_visible_from) : null;
        const until = c.announcement_visible_until ? new Date(c.announcement_visible_until) : null;
        if ((from && now < from) || (until && now > until)) {
          setShowAnnouncement(false);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!q || q.length < 1) {
      setResults({ products: [], posts: [] });
      return;
    }
    const t = setTimeout(() => {
      suggest(q).then(setResults).catch(() => {});
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/shop?search=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  return (
    <>
      {/* Announcement bar */}
      {showAnnouncement && (
        <div
          className="bg-[#c3ba9e] text-[#1A1A1A] text-[11px] tracking-[0.28em] uppercase py-2 text-center font-body border-b border-[#a89e80]"
          data-testid="announcement-bar"
        >
          {announcement}
        </div>
      )}

      <header
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled
            ? "bg-[#c3ba9e]/95 backdrop-blur-xl border-b border-[#a89e80]"
            : "bg-[#c3ba9e] border-b border-transparent"
        }`}
        data-testid="site-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger asChild>
              <button
                className="md:hidden p-2 -ml-2 text-[#1A1A1A]"
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
              >
                <Menu size={22} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#c3ba9e] border-r border-[#a89e80] w-[82%]">
              <SheetHeader>
                <SheetTitle className="font-serif-display text-3xl">Asvasa</SheetTitle>
              </SheetHeader>
              <nav className="mt-8 flex flex-col gap-5">
                {NAV.map((n) => (
                  <Link
                    key={n.label}
                    to={n.to}
                    className="text-lg font-serif-display"
                    data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  >
                    {n.label}
                  </Link>
                ))}
                <div className="border-t border-[#a89e80] pt-5 mt-2 space-y-4">
                  <Link to={user && user !== false ? "/account" : "/login"} className="block text-lg font-serif-display" data-testid="mobile-nav-account">
                    {user && user !== false ? "My Account" : "Sign in / Register"}
                  </Link>
                  <Link to="/account" className="block text-sm tracking-[0.22em] uppercase text-[#4A4A4A]" data-testid="mobile-nav-wishlist">
                    Wishlist
                  </Link>
                </div>
              </nav>
            </SheetContent>
          </Sheet>

          {/* Desktop left nav */}
          <nav className="hidden md:flex items-center gap-8 text-[12px] tracking-[0.22em] uppercase font-body">
            {NAV.slice(0, 3).map((n) => (
              <Link
                key={n.label}
                to={n.to}
                className="link-underline text-[#1A1A1A] hover:text-[#1A1A1A] transition-colors"
                data-testid={`nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          {/* Logo — centered */}
          <Link
            to="/"
            className="absolute left-1/2 -translate-x-1/2 flex items-center"
            data-testid="site-logo-link"
          >
            <img
              src={LOGO_URL}
              alt="Asvasa"
              className="h-9 md:h-11 w-auto"
              style={{ objectFit: "contain" }}
            />
          </Link>

          {/* Right nav */}
          <div className="flex items-center gap-4 md:gap-6">
            <nav className="hidden md:flex items-center gap-8 text-[12px] tracking-[0.22em] uppercase font-body">
              {NAV.slice(3).map((n) => (
                <Link
                  key={n.label}
                  to={n.to}
                  className="link-underline text-[#1A1A1A] hover:text-[#1A1A1A] transition-colors"
                  data-testid={`nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </Link>
              ))}
            </nav>
            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="text-[#1A1A1A] hover:text-[#1A1A1A] transition-colors"
              data-testid="nav-search-button"
            >
              <Search size={19} />
            </button>
            <Link
              to="/account"
              aria-label="Wishlist"
              className="hidden md:inline-flex text-[#1A1A1A] hover:text-[#1A1A1A] transition-colors"
              data-testid="nav-wishlist"
            >
              <Heart size={19} />
            </Link>
            <Link
              to={user && user !== false ? "/account" : "/login"}
              aria-label="Account"
              className="hidden md:inline-flex text-[#1A1A1A] hover:text-[#1A1A1A] transition-colors"
              data-testid="nav-account"
            >
              <User size={19} />
            </Link>
            <button
              onClick={() => setIsOpen(true)}
              className="relative text-[#1A1A1A] hover:text-[#1A1A1A] transition-colors"
              aria-label="Open cart"
              data-testid="nav-cart-button"
            >
              <ShoppingBag size={19} />
              {count > 0 && (
                <span
                  className="absolute -top-2 -right-2 text-[10px] bg-[#1A1A1A] text-[#c3ba9e] w-4 h-4 flex items-center justify-center rounded-full"
                  data-testid="nav-cart-count"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Search overlay */}
      {searchOpen && (
        <div
          className="fixed inset-0 z-50 bg-[#c3ba9e]/98 backdrop-blur-md"
          data-testid="search-overlay"
        >
          <div className="max-w-3xl mx-auto px-6 pt-24 md:pt-32">
            <div className="flex items-center justify-between mb-8">
              <span className="eyebrow">Search Asvasa</span>
              <button
                onClick={() => setSearchOpen(false)}
                aria-label="Close search"
                className="text-[#1A1A1A]"
                data-testid="search-close"
              >
                <X size={22} />
              </button>
            </div>
            <form onSubmit={submitSearch}>
              <Input
                autoFocus
                placeholder="Search kurtas, shirts, fabrics, stories…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="border-0 border-b border-[#a89e80] rounded-none bg-transparent text-3xl md:text-5xl font-serif-display h-20 px-0 focus-visible:ring-0 focus-visible:border-[#6b6353]"
                data-testid="search-input"
              />
            </form>

            {(results.products.length > 0 || results.posts.length > 0) && (
              <div className="grid md:grid-cols-2 gap-8 mt-10">
                {results.products.length > 0 && (
                  <div>
                    <p className="eyebrow mb-4">Products</p>
                    <ul className="space-y-3">
                      {results.products.map((p) => (
                        <li key={p.id}>
                          <Link
                            to={`/shop/${p.slug}`}
                            onClick={() => setSearchOpen(false)}
                            className="font-serif-display text-xl hover:text-[#1A1A1A]"
                            data-testid={`search-result-product-${p.slug}`}
                          >
                            {p.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {results.posts.length > 0 && (
                  <div>
                    <p className="eyebrow mb-4">Journal</p>
                    <ul className="space-y-3">
                      {results.posts.map((p) => (
                        <li key={p.slug}>
                          <Link
                            to={`/blog/${p.slug}`}
                            onClick={() => setSearchOpen(false)}
                            className="font-serif-display text-xl hover:text-[#1A1A1A]"
                            data-testid={`search-result-post-${p.slug}`}
                          >
                            {p.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
